/**
 * Invited Profile Step Component
 *
 * Step 1: Collect invited user's name.
 * Simplified version of ProfileStep without organization name field.
 *
 * @module components/onboarding/InvitedProfileStep
 */

import { useState } from 'react';
import { Button, Input } from '@/components/ui';
import { ArrowRight } from 'lucide-react';

// =============================================================================
// Types
// =============================================================================

export interface InvitedProfileStepProps {
  /** Organization name the user is joining */
  organizationName: string;
  /** Callback when profile is submitted */
  onSubmit: (data: { firstName: string; lastName: string }) => Promise<void>;
  /** Whether the form is loading */
  loading: boolean;
  /** Error message if any */
  error: string | null;
  /** Initial values (for returning users) */
  initialValues?: {
    firstName?: string;
    lastName?: string;
  };
}

// =============================================================================
// Component
// =============================================================================

export function InvitedProfileStep({
  organizationName,
  onSubmit,
  loading,
  error,
  initialValues,
}: InvitedProfileStepProps) {
  const [firstName, setFirstName] = useState(initialValues?.firstName || '');
  const [lastName, setLastName] = useState(initialValues?.lastName || '');

  const isValid = firstName.trim().length > 0 && lastName.trim().length > 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValid || loading) return;

    await onSubmit({
      firstName: firstName.trim(),
      lastName: lastName.trim(),
    });
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-display font-semibold text-gray-900">
          Welcome to {organizationName}!
        </h2>
        <p className="text-gray-500 mt-2">
          Let's set up your profile.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Input
            label="First name"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            placeholder="John"
            autoFocus
            required
          />
          <Input
            label="Last name"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            placeholder="Doe"
            required
          />
        </div>

        {error && (
          <p className="text-sm text-error-600 bg-error-50 px-3 py-2 rounded-lg">
            {error}
          </p>
        )}

        <Button
          type="submit"
          loading={loading}
          disabled={!isValid}
          rightIcon={<ArrowRight className="h-4 w-4" />}
        >
          Continue
        </Button>
      </form>
    </div>
  );
}
